import React, { useEffect, useRef, useState } from 'react';
import { adminService, Banner, Category } from './adminService';
import { BannerPlacement, ShowcaseFilter, SHOWCASE_FILTERS, showcaseCatalogUrl } from '../banners/types';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const AdminBannersPage = () => {
  const [placement, setPlacement] = useState<BannerPlacement>('home');
  return (
    <div className="space-y-6 text-gray-900">
      <div className="flex gap-2" role="tablist" aria-label="Раздел карусели">
        {(['home', 'playstation'] as const).map(value => (
          <Button key={value} role="tab" aria-selected={placement === value}
            onClick={() => setPlacement(value)} variant="outline"
            className={`rounded-xl border px-5 focus-visible:ring-gray-500 ${placement === value
              ? 'border-gray-900 bg-gray-900 text-white hover:bg-gray-800 hover:text-white'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900'}`}>
            {value === 'home' ? 'Главная' : 'PS Store'}
          </Button>
        ))}
      </div>
      <BannerList key={placement} placement={placement} />
    </div>
  );
};

const BannerList = ({ placement }: { placement: BannerPlacement }) => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [b, c] = await Promise.all([
        adminService.fetchBanners(placement),
        placement === 'home' ? adminService.fetchCategories() : Promise.resolve([]),
      ]);
      setBanners(b);
      setCategories(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      setBusy(true);
      const created = await adminService.createBanner({ placement, ...(placement === 'playstation' ? { showcaseFilter: 'brandNew' } : {}) });
      setBanners(prev => [...prev, created]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось создать карточку');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить карточку?')) return;
    try {
      setBusy(true);
      await adminService.deleteBanner(id);
      setBanners(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  const patchBanner = (updated: Banner) =>
    setBanners(prev => prev.map(b => (b.id === updated.id ? updated : b)));

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= banners.length) return;
    const next = [...banners];
    [next[index], next[target]] = [next[target], next[index]];
    setBanners(next);
    try {
      setBusy(true);
      await adminService.reorderBanners(next.map(b => b.id), placement);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить порядок');
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Баннеры карусели — {placement === 'home' ? 'Главная' : 'PS Store'}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {placement === 'playstation' ? 'Карточки PS Store: квадратная картинка, цель перехода и порядок. Выберите подборку игр PS4/PS5 или карты пополнения.' : 'Карточки главной карусели: картинка, цель (категория, шеринг или ссылка) и порядок.'}
          </p>
        </div>
        <Button
          type="button"
          onClick={handleCreate}
          disabled={busy || loading}
          className="rounded-xl bg-gray-900 px-6 text-white hover:bg-gray-800 disabled:opacity-40"
        >
          + Добавить карточку
        </Button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-600 shadow-sm">Загрузка…</div>
      ) : banners.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-600 shadow-sm">
          Карточек пока нет. Нажми «Добавить карточку».
        </div>
      ) : (
        <div className="grid gap-4">
          {banners.map((b, i) => (
            <BannerCard
              key={b.id}
              banner={b}
              categories={categories}
              index={i}
              first={i === 0}
              last={i === banners.length - 1}
              busy={busy}
              setBusy={setBusy}
              onError={setError}
              onPatch={patchBanner}
              onDelete={handleDelete}
              onUp={() => move(i, -1)}
              onDown={() => move(i, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BannerCard: React.FC<{
  banner: Banner;
  categories: Category[];
  index: number;
  first: boolean;
  last: boolean;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onError: (v: string | null) => void;
  onPatch: (b: Banner) => void;
  onDelete: (id: number) => void;
  onUp: () => void;
  onDown: () => void;
}> = ({ banner, categories, index, first, last, busy, setBusy, onError, onPatch, onDelete, onUp, onDown }) => {
  const isPlayStation = banner.placement === 'playstation';
  const [showcaseFilter, setShowcaseFilter] = useState<ShowcaseFilter>(banner.showcaseFilter ?? 'brandNew');
  const [mode, setMode] = useState<'category' | 'sharing' | 'url'>(banner.isSharing ? 'sharing' : banner.externalUrl && !banner.categoryId ? 'url' : 'category');
  const [categoryId, setCategoryId] = useState<number | ''>(banner.categoryId ?? '');
  const [url, setUrl] = useState(banner.externalUrl ?? '');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setShowcaseFilter(banner.showcaseFilter ?? 'brandNew');
    setCategoryId(banner.categoryId ?? '');
    setUrl(banner.externalUrl ?? '');
    setMode(banner.isSharing ? 'sharing' : banner.externalUrl && !banner.categoryId ? 'url' : 'category');
  }, [banner.categoryId, banner.externalUrl, banner.isSharing, banner.showcaseFilter]);

  const dirty = isPlayStation ? showcaseFilter !== banner.showcaseFilter :
    mode === 'category'
      ? (categoryId === '' ? banner.categoryId != null : categoryId !== banner.categoryId) || banner.externalUrl != null || banner.isSharing
      : mode === 'sharing'
        ? !banner.isSharing
        : (url.trim() !== (banner.externalUrl ?? '')) || banner.categoryId != null || banner.isSharing;

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBusy(true);
      onError(null);
      const updated = await adminService.uploadBannerImage(banner.id, file);
      onPatch(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Не удалось загрузить картинку');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSaveTarget = async () => {
    try {
      setBusy(true);
      onError(null);
      const payload = isPlayStation
        ? { showcaseFilter, categoryId: null, externalUrl: null, isSharing: false }
        : mode === 'category'
          ? { categoryId: categoryId === '' ? null : Number(categoryId), externalUrl: null, isSharing: false }
          : mode === 'sharing'
            ? { categoryId: null, externalUrl: null, isSharing: true }
            : { categoryId: null, externalUrl: url.trim() || null, isSharing: false };
      const updated = await adminService.updateBanner(banner.id, { ...payload, placement: banner.placement });
      onPatch(updated);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Не удалось сохранить цель');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:flex-row">
      {/* Порядок */}
      <div className="flex flex-row items-center gap-1 sm:flex-col">
        <Button type="button" variant="outline" onClick={onUp} disabled={busy || first}
          className="h-8 w-8 rounded-lg border border-gray-200 bg-white p-0 text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30">↑</Button>
        <span className="text-xs text-gray-600">#{index + 1}</span>
        <Button type="button" variant="outline" onClick={onDown} disabled={busy || last}
          className="h-8 w-8 rounded-lg border border-gray-200 bg-white p-0 text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30">↓</Button>
      </div>

      {/* Превью + загрузка */}
      <div className="w-full sm:w-56 shrink-0">
        <div className="aspect-square overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
          {banner.imageUrl ? (
            <img src={banner.imageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-gray-600">Нет картинки</div>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}
          className="mt-2 w-full rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900">
          {banner.imageUrl ? 'Заменить картинку' : 'Загрузить картинку'}
        </Button>
      </div>

      {/* Цель */}
      <div className="flex-1 space-y-3">
        {!isPlayStation && <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" checked={mode === 'sharing'} onChange={() => setMode('sharing')} />
            Шеринг
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={mode === 'category'} onChange={() => setMode('category')} />
            Категория
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={mode === 'url'} onChange={() => setMode('url')} />
            Ссылка
          </label>
        </div>}

        {isPlayStation ? (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700" htmlFor={`showcase-${banner.id}`}>Цель перехода</label>
            <select id={`showcase-${banner.id}`} value={showcaseFilter} onChange={e => setShowcaseFilter(e.target.value as ShowcaseFilter)}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
              {Object.entries(SHOWCASE_FILTERS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <Link to={showcaseCatalogUrl(showcaseFilter)} className="inline-block text-sm text-blue-600 hover:underline">{showcaseFilter === 'topUpCards' ? 'Посмотреть карты пополнения →' : 'Посмотреть подборку PS4 / PS5 →'}</Link>
          </div>
        ) : mode === 'category' ? (
          <select
            value={categoryId}
            onChange={e => setCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white"
          >
            <option value="">— не выбрана —</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>{c.name} (/{c.slug})</option>
            ))}
          </select>
        ) : mode === 'sharing' ? (
          <div className="rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-700">
            Откроет каталог игр с фильтром «Шеринг».
          </div>
        ) : (
          <input
            type="url"
            placeholder="https://…"
            value={url}
            onChange={e => setUrl(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-400 focus:bg-white"
          />
        )}

        <div className="flex items-center gap-2">
          <Button type="button" onClick={handleSaveTarget} disabled={busy || !dirty}
            className="rounded-xl bg-gray-900 px-4 text-white hover:bg-gray-800 disabled:opacity-30">Сохранить цель</Button>
          <Button type="button" variant="outline" onClick={() => onDelete(banner.id)} disabled={busy}
            className="rounded-xl border border-red-200 bg-white px-4 text-red-700 hover:bg-red-50 hover:text-red-800">Удалить</Button>
        </div>
      </div>
    </div>
  );
};

export default AdminBannersPage;
